import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { cn } from "@arcadeai/design-system/lib/utils";
import { AlertCircle, Clock, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { authClient, sessionQueryOptions } from "@/lib/auth-client";
import { useAuthError } from "@/lib/auth-recovery";
import { queryClient } from "@/lib/query-client";

export function AuthErrorDialog() {
  const open = useAuthError();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) {
      // Remove (not null-out) the cached session so the next `ensureQueryData`
      // call refetches from the server. Setting it to null would make route
      // guards like `_authenticated` see an explicit logged-out state and
      // redirect away even when re-auth has already restored the cookies.
      queryClient.removeQueries({ queryKey: sessionQueryOptions.queryKey });
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleSignIn = async () => {
    setPending(true);
    setFailed(false);

    // Clear local cookies without ending Ory's SSO — preserved SSO is
    // what lets the OAuth call below auto-consent.
    try {
      await authClient.signOut();
    } catch {
      // Best-effort.
    }

    // Return to the page the dialog appeared on.
    const callbackURL = window.location.href;
    let oauthUrl: string | null = null;
    let didFail = false;
    try {
      const result = await authClient.signIn.oauth2({
        providerId: "oidc",
        callbackURL,
      });
      if (result?.error) {
        didFail = true;
      } else {
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        oauthUrl = (result?.data as { url?: string } | undefined)?.url ?? null;
      }
    } catch {
      didFail = true;
    }

    // If we couldn't even get an OAuth URL, switch the dialog to a failure
    // state so the user has an explicit recovery path. We deliberately do
    // NOT auto-navigate to /login here — a hidden fallback that fires after
    // a timer could interrupt a slow-but-otherwise-fine SSO flow.
    if (didFail || !oauthUrl) {
      setPending(false);
      setFailed(true);
      return;
    }

    // Keep the overlay up while the browser navigates away. The page unload
    // will destroy it naturally. Crucially we do NOT close the dialog or
    // schedule a fallback here: a synchronous close before the browser
    // starts navigating gives the user a brief interactive window where a
    // SPA click can bounce them through /login.
    window.location.assign(oauthUrl);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div
      aria-describedby="auth-error-description"
      aria-labelledby="auth-error-title"
      aria-modal="true"
      className="fade-in fixed inset-0 z-[100] flex animate-in items-center justify-center bg-background p-4 duration-200"
      role="alertdialog"
    >
      <Card className="fade-in zoom-in-95 w-full max-w-md animate-in duration-200 md:max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
              {failed ? (
                <AlertCircle className="size-3.5 text-muted-foreground" />
              ) : (
                <Clock className="size-3.5 text-muted-foreground" />
              )}
            </div>
            <CardTitle className="text-lg" id="auth-error-title">
              {failed ? "Couldn't sign in" : "Session expired"}
            </CardTitle>
          </div>
          <CardDescription id="auth-error-description">
            {failed
              ? "Something went wrong while signing you back in. Refresh the page to try again."
              : "Your session has expired. Please sign in again to continue."}
          </CardDescription>
        </CardHeader>
        <CardFooter>
          {failed ? (
            <Button
              autoFocus
              className="w-full"
              onClick={handleRefresh}
              type="button"
            >
              Refresh page
            </Button>
          ) : (
            <Button
              autoFocus
              className="w-full"
              disabled={pending}
              onClick={handleSignIn}
              type="button"
            >
              <span className="grid grid-cols-1 grid-rows-1 place-items-center">
                <span
                  aria-hidden={pending}
                  className={cn(
                    "col-start-1 row-start-1 transition-opacity duration-150",
                    pending ? "opacity-0" : "opacity-100"
                  )}
                >
                  Sign in again
                </span>
                <span
                  aria-hidden={!pending}
                  className={cn(
                    "col-start-1 row-start-1 inline-flex items-center gap-2 transition-opacity duration-150",
                    pending ? "opacity-100" : "opacity-0"
                  )}
                >
                  <Loader2 className="size-4 animate-spin" />
                  Signing in…
                </span>
              </span>
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}
